<template>
    <full-page-spinner v-if="loading"></full-page-spinner>
    <div v-else class="py-4 container-fluid mx-auto">
        <h1 class="h1 pt-4 display-2 fw-normal text-primary text-center">
            รับของขวัญพรีเมี่ยม
        </h1>

        <div class="row">
            <div
                v-for="index in 6"
                :key="index"
                class="col-6 position-relative my-2"
            >
                <img
                    :src="`image/gift-${index}.png`"
                    :alt="`gift-option-${index}`"
                    :class="[boxState(index), 'w-100 gift-shadow rounded-4']"
                    @click="handleBoxClick(index)"
                    @load="setImageSize($event, index)"
                />
                <template v-if="isRedeemedBox(index)">
                    <div class="used-text">แลกแล้ว</div>
                </template>
                <template v-else-if="isAccumulatedBox(index)">
                    <div
                        class="pending-overlay"
                        :style="pendingOverlayStyles[index]"
                    ></div>
                    <div class="pending-sash">สะสมแล้ว</div>
                </template>
            </div>
        </div>
    </div>
    <redemption-form
        ref="redemptionForm"
        :showDialog="showDialog"
        :onClose="handleCloseDialog"
        :shopOptions="shopOptions"
        :selectedShop="selectedShop"
        :isFormComplete="isFormComplete"
        :maxReceiptBoxes="maxReceiptBoxes"
        :latestRedemptionData="latestRedemptionData"
        @update:showDialog="showDialog = $event"
        @update:selectedShop="selectedShop = $event"
        @submit="handleFormSubmit"
        :submitting="loading"
        @show-reward-modal="openRewardModal()"
    />
    <reward-modal
        v-if="showRewardModal"
        @close="closeRewardModal"
        @submit="handleRewardSubmit"
        :submitting="loading"
        :rewards="rewards"
        :available-points="availablePoints"
    />
</template>

<script>
import '@/assets/scss/vue-select.css';
import * as api from '@/services';
import vClickOutside from 'click-outside-vue3';
import { mapState } from 'vuex';
import FullPageSpinner from '@/views/components/FullPageSpinner.vue';
import RedemptionForm from '@/views/components/forms/LongRedemptionForm.vue';
import RewardModal from '@/views/components/forms/RewardModal.vue';
import { uploadFile } from '@/services';
import { enrichRewardFromApi } from '@/data/reward-catalog';

const MAX_BOXES = 6;

export default {
    directives: {
        clickOutside: vClickOutside.directive,
    },
    components: {
        FullPageSpinner,
        RedemptionForm,
        RewardModal,
    },
    data() {
        return {
            customer: null,
            showDialog: false,
            showRewardModal: false,
            shopOptions: [],
            selectedShop: '',
            loading: true,
            isFormComplete: false,
            pendingOverlayStyles: {},
            latestRedemptionData: null,
            rewards: [],
        };
    },
    methods: {
        /** Gift redeemed (grey) — maps to customer.usedPoints. */
        redeemedBoxes() {
            return Math.min(MAX_BOXES, this.customer?.usedPoints ?? 0);
        },
        /** Approved points (pink สะสมแล้ว) — maps to customer.points. */
        accumulatedBoxes() {
            return Math.min(
                MAX_BOXES - this.redeemedBoxes(),
                this.customer?.points ?? 0,
            );
        },
        /** Total filled slots: grey (usedPoints) + pink (points). */
        filledBoxes() {
            return Math.min(
                MAX_BOXES,
                this.redeemedBoxes() + this.accumulatedBoxes(),
            );
        },
        isRedeemedBox(index) {
            return index <= this.redeemedBoxes();
        },
        isAccumulatedBox(index) {
            const pinkEnd = this.redeemedBoxes() + this.accumulatedBoxes();
            return index > this.redeemedBoxes() && index <= pinkEnd;
        },
        isEmptyBox(index) {
            return index > this.filledBoxes();
        },
        canRedeemGift(index) {
            return this.isAccumulatedBox(index);
        },
        boxState(index) {
            if (this.isRedeemedBox(index)) {
                return 'used';
            }
            if (this.isAccumulatedBox(index)) {
                return 'accumulated';
            }
            return '';
        },
        mapRewardsFromApi(results) {
            return results
                .filter((reward) => reward.isActive)
                .sort((a, b) => a.pointsCost - b.pointsCost)
                .map((reward) => enrichRewardFromApi(reward));
        },
        setImageSize(event, index) {
            if (this.isAccumulatedBox(index)) {
                const img = event.target;
                this.pendingOverlayStyles[index] = {
                    width: `${img.offsetWidth}px`,
                    height: `${img.offsetHeight}px`,
                };
            }
        },
        handleCloseDialog() {
            this.showDialog = !this.showDialog;
        },
        handleBoxClick(index) {
            if (this.isRedeemedBox(index)) {
                return;
            }

            if (this.canRedeemGift(index)) {
                this.openRewardModal();
                return;
            }

            if (
                this.isEmptyBox(index) &&
                this.maxReceiptBoxes > 0 &&
                this.filledBoxes() < MAX_BOXES
            ) {
                this.showDialog = true;
            }
        },
        openRewardModal() {
            if (!this.latestRedemptionData) {
                alert('ไม่พบข้อมูลที่อยู่จัดส่ง กรุณาส่งใบเสร็จและกรอกที่อยู่ก่อนแลกของรางวัล');
                if (this.maxReceiptBoxes > 0) {
                    this.showDialog = true;
                }
                return;
            }

            if (this.availablePoints <= 0) {
                return;
            }

            this.showRewardModal = true;
        },
        closeRewardModal() {
            this.showRewardModal = false;
        },
        closeLiffWindow() {
            const liff = this.$liff ?? window.liff;
            if (liff?.closeWindow) {
                liff.closeWindow();
            }
        },
        buildRedeemPayload(reward, address) {
            return {
                customerId: this.userId,
                items: [{ rewardId: reward.id, quantity: 1 }],
                full_name: address.full_name,
                nickname: address.nickname,
                house_number: address.house_number,
                group: address.group,
                village_or_alley: address.village_or_alley,
                building: address.building,
                floor: address.floor,
                road: address.road,
                subdistrict: address.subdistrict,
                district: address.district,
                province: address.province,
                postal_code: address.postal_code,
                telephone_number: address.telephone_number,
                line_id: address.line_id,
            };
        },
        getRedeemErrorMessage(error) {
            const message = error?.response?.data?.message;
            const text = Array.isArray(message) ? message.join(', ') : message;

            if (text === 'Insufficient points') {
                return 'คะแนนไม่เพียงพอสำหรับแลกของรางวัลนี้';
            }
            if (text === 'One or more rewards are invalid or inactive') {
                return 'ของรางวัลที่เลือกไม่พร้อมให้แลก';
            }

            return text || 'ไม่สามารถแลกของรางวัลได้ กรุณาลองใหม่อีกครั้ง';
        },
        async sendLineGiftMessage(reward) {
            const liff = this.$liff ?? window.liff;
            if (!liff?.isInClient?.() || !reward?.message) {
                return false;
            }

            try {
                await liff.sendMessages([
                    {
                        type: 'text',
                        text: reward.message,
                    },
                ]);
                return true;
            } catch (error) {
                console.error('Failed to send LINE message:', error);
                return false;
            }
        },
        async handleRewardSubmit(reward) {
            this.loading = true;
            try {
                const address = this.latestRedemptionData;
                if (!address) {
                    alert('ไม่พบข้อมูลที่อยู่จัดส่ง กรุณาส่งใบเสร็จและกรอกที่อยู่ก่อนแลกของรางวัล');
                    return;
                }

                await api.postRewardRedemptionRedeem(
                    this.buildRedeemPayload(reward, address),
                );

                const messageSent = await this.sendLineGiftMessage(reward);

                const res = await api.getCustomerById(this.userId);
                this.customer = res.data;

                if (messageSent) {
                    this.closeLiffWindow();
                }
            } catch (error) {
                console.error('Reward submission failed:', error);
                alert(this.getRedeemErrorMessage(error));
            } finally {
                this.loading = false;
                this.showRewardModal = false;
            }
        },
        async handleFormSubmit(submissionData) {
            this.loading = true;
            try {
                const resRedemption = await api.postRedemptions({
                    redeemed_by: this.userId,
                    redeemed_at: this.selectedShop,
                });

                let imageUrl = null;
                if (submissionData.receiptImage) {
                    const img = submissionData.receiptImage;
                    const newFileName = `${this.userId}-redemption-${this.filledBoxes() + 1}-${resRedemption.data.id}-${new Date().getTime()}.${img.name.split('.').pop()}`;
                    const renamedFile = new File([img], newFileName, {
                        type: img.type,
                        lastModified: new Date().getTime(),
                    });
                    const uploadRes = await uploadFile(`${this.userId}`, renamedFile);
                    imageUrl = uploadRes.data.url;
                }

                const submissionPayload = {
                    ...submissionData,
                    receipt_picture_url: imageUrl,
                    user_id: this.userId,
                };
                delete submissionPayload.receiptImage;
                await api.postRedemptionForm(submissionPayload);

                const res = await api.getCustomerById(this.userId);
                this.customer = res.data;
                if (
                    this.customer?.redemptionForm?.length > 0
                ) {
                    this.latestRedemptionData =
                        this.customer.redemptionForm[this.customer.redemptionForm.length - 1];
                }

                this.showDialog = false;
            } catch (error) {
                console.error('Submission failed:', error);
            } finally {
                this.loading = false;
            }
        },
    },
    computed: {
        ...mapState(['userId']),
        availablePoints() {
            if (!this.customer) {
                return 0;
            }
            return this.customer.points ?? 0;
        },
        /** Empty campaign slots the user can still earn (not admin remainingBoxes). */
        maxReceiptBoxes() {
            const emptySlots = MAX_BOXES - this.filledBoxes();
            return Math.max(0, Math.min(3, emptySlots));
        },
    },
    mounted() {
        api.getShops()
            .then((res) => {
                this.shops = res.data.results;
            })
            .then(() => {
                this.shopOptions = this.shops.map((x) => ({
                    label: `(${x.adjustedId}) ${x.name}`,
                    adjustedId: x.adjustedId,
                }));
            });

        api.getRewards({ take: 20 })
            .then((res) => {
                this.rewards = this.mapRewardsFromApi(res.data.results || []);
            })
            .catch((error) => {
                console.error('Failed to load rewards:', error);
            });

        api.getCustomerById(this.userId).then((res) => {
            this.customer = res.data;
            if (this.customer?.redemptionForm?.length > 0) {
                this.latestRedemptionData =
                    this.customer.redemptionForm[this.customer.redemptionForm.length - 1];
            }
            this.loading = false;
        });
    },
};
</script>

<style>
.gift-shadow {
    box-shadow: 0 0 0.5rem rgb(0 0 0 / 10%) !important;
    border-radius: 1.1rem;
}

.used {
    -webkit-filter: grayscale(100%);
    filter: grayscale(100%);
}

.accumulated {
    cursor: pointer;
}

.used-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: rgba(230, 230, 230, 0.9);
    color: #454545;
    overflow: hidden;
    white-space: nowrap;
    text-align: center;
    font-size: 0.8rem;
    font-weight: 500;
    padding: 4px 16px;
    pointer-events: none;
}

.pending-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 192, 203, 0.35);
    border-radius: 1.1rem;
    margin: 0 auto;
    pointer-events: none;
}

.pending-sash {
    position: absolute;
    background-color: rgba(255, 192, 203, 0.85);
    color: rgb(244, 25, 98);
    overflow: hidden;
    white-space: nowrap;
    text-align: center;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 0.8rem;
    font-weight: 500;
    padding: 4px 12px;
    pointer-events: none;
}

.rounded-4 {
    border-radius: 12%;
}

</style>
